import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtMT } from "@/lib/mountainTime";
import { labHorses, linkPost, syncPost, patchPost, errText } from "@/lib/fbLabApi";
import { PostDetail } from "./PostDetail";
import { JobsList } from "./JobsList";
import { SyncPlan } from "@/components/sync/SyncPlan";
import { SyncScheduleForm } from "@/components/sync/SyncScheduleForm";
import { savePostSchedule, getSchedule } from "@/lib/syncScheduleApi";

function PostSchedule({ p, onChange }) {
  const [open, setOpen] = useState(false);
  const [limits, setLimits] = useState(null);
  useEffect(() => { if (open && !limits) { getSchedule().then((d) => setLimits(d.limits)).catch(() => {}); } }, [open, limits]);
  const save = async (body) => { try { await savePostSchedule(p.id, body); toast.success("Schedule saved for this auction"); setOpen(false); onChange(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="text-xs">
      <button data-testid={`lab-post-schedule-toggle-${p.id}`} onClick={() => setOpen(!open)} className="text-[#1877F2] hover:underline">{open ? "Close schedule" : p.schedule.override ? "Edit this auction's custom schedule" : "Customize this auction's schedule"}</button>
      {p.schedule.override && <button data-testid={`lab-post-schedule-reset-${p.id}`} onClick={() => save({ use_company: true })} className="ml-3 text-stone-600 hover:underline">Use company schedule</button>}
      {open && limits && <div className="mt-2 border rounded-lg p-2"><SyncScheduleForm value={p.schedule.settings} limits={limits} onSave={(s) => save({ schedule: s })} testid={`lab-post-sched-${p.id}`} /></div>}
    </div>
  );
}

function LinkPost({ hasGroup, onChange }) {
  const [horses, setHorses] = useState([]);
  const [f, setF] = useState({ horse_id: "", post_url: "" });
  useEffect(() => { labHorses().then(setHorses).catch(() => {}); }, []);
  const go = async () => {
    try {
      const r = await linkPost(f);
      setF({ ...f, post_url: "" });
      if (r.pending) toast.info(`${r.message} It's linked only if Facebook shows it's a post in your Primary Facebook Group — the result appears under Share link checks below.`);
      else toast.success("Post linked and comment sync turned on");
      onChange();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div data-testid="lab-link-post" className="grid sm:grid-cols-[1fr_2fr_auto] gap-2">
      <Select value={f.horse_id} onValueChange={(v) => setF({ ...f, horse_id: v })}><SelectTrigger data-testid="lab-link-horse"><SelectValue placeholder="Horse" /></SelectTrigger>
        <SelectContent>{horses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent></Select>
      <Input data-testid="lab-link-url" placeholder="Post link (group post link or Facebook's Copy Link /share/p/…)" value={f.post_url} onChange={(e) => setF({ ...f, post_url: e.target.value })} />
      <Button data-testid="lab-link-submit" disabled={!hasGroup || !f.horse_id || !f.post_url} onClick={go}>Link as auction post</Button>
    </div>
  );
}

function PostRow({ p, open, onOpen, onChange }) {
  const sync = async () => { try { const r = await syncPost(p.id); toast.success(r.message || "Sync queued"); onChange(); } catch (e) { toast.error(errText(e)); } };
  const auto = async (v) => { try { await patchPost(p.id, { auto_sync: v }); onChange(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div data-testid={`lab-post-${p.id}`} className="border rounded-lg p-3 text-sm space-y-2">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <button data-testid={`lab-post-open-${p.id}`} onClick={onOpen} className="font-semibold hover:underline text-left">{p.horse_name} · Auction post <span className="text-xs font-normal text-[#1877F2]">{open ? "Hide comments & bids" : "Show comments & bids"}</span></button>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs"><Switch data-testid={`lab-post-auto-${p.id}`} checked={!!p.auto_sync} onCheckedChange={auto} />Auto-sync (staged schedule)</label>
          <Button data-testid={`lab-post-sync-${p.id}`} size="sm" onClick={sync} className="rounded-full"><RefreshCw className="h-3.5 w-3.5 mr-1" />Sync Comments</Button>
        </div>
      </div>
      <p data-testid={`lab-post-sync-status-${p.id}`} className="text-xs text-stone-600">
        Last synced: {p.last_synced_at ? fmtMT(p.last_synced_at) : "never"} · Status: {p.last_sync_status || "—"}{p.access === "inaccessible" ? " · post not accessible" : ""}
        {p.last_sync_summary ? ` · ${p.last_sync_summary}` : ""}
      </p>
      {p.last_sync_error && <p data-testid={`lab-post-sync-error-${p.id}`} className="text-xs text-red-700">{p.last_sync_error}</p>}
      {p.schedule && <SyncPlan plan={p.schedule} id={p.id} />}
      {p.schedule && <PostSchedule p={p} onChange={onChange} />}
      <a href={p.post_url} target="_blank" rel="noreferrer" className="text-xs text-[#1877F2] hover:underline break-all">{p.post_url}</a>
      {open && <PostDetail pid={p.id} />}
    </div>
  );
}

export function PostsCard({ posts, jobs = [], hasGroup, onChange }) {
  const [open, setOpen] = useState(undefined);
  const shown = open === undefined ? posts[0]?.id : open; // first post opens automatically
  const checks = jobs.filter((j) => j.kind === "resolve").slice(0, 5);
  return (
    <section data-testid="lab-posts" className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
      <h2 className="font-semibold">Auction posts, comments and bids</h2>
      <p className="text-xs text-stone-600">Facebook remains the official bidding platform. Comments are captured as evidence; possible bids are flagged for your review and never rejected automatically.</p>
      <LinkPost hasGroup={hasGroup} onChange={onChange} />
      {checks.length > 0 && <div data-testid="lab-share-checks"><p className="text-xs font-semibold text-stone-600 mb-1">Share link checks</p><JobsList jobs={checks} onChange={onChange} /></div>}
      {posts.length ? posts.map((p) => <PostRow key={p.id} p={p} open={shown === p.id} onOpen={() => setOpen(shown === p.id ? null : p.id)} onChange={onChange} />)
        : <p className="text-sm text-stone-500">No posts yet — publish one or link an existing test post.</p>}
    </section>
  );
}
